/* eslint-disable max-statements */
import { unescapeHTML } from '@ds/swymkit/utils'
import { defineStore } from 'pinia'

import type { CreateCommunityData, ICommunity } from '../../community/interfaces/community'

import { transformRTConvToCommunity } from '~/entities/container/conversation/api/conversations'
import type { IConversation } from '~/entities/container/conversation/interfaces/conversation'
import type { IUser } from '~/entities/user/user/interfaces/user'
import { UserState } from '~/entities/user/user/interfaces/user'
import { useUsersStore } from '~/entities/user/user/stores/users'

import { useContextManager } from '~/features-shared/config-manager/composables/context-manager'
import { EContext } from '~/features-shared/config-manager/interfaces/contexts'
import { useContentsStoreV2 } from '~/features-shared/content/stores/v2-contents'
import { useTemporaryMarks } from '~/features-shared/conversation/composables/temporary-marks'
import { getIdFromSubjectUri } from '~/features-shared/libs/utils/subject-uri-utils'
import type { IMember } from '~/features-shared/member/interfaces/search'
import { getMaxUsersAllowed, getRTConversationId } from '~/features-shared/rtconv/api/rtconversation-api'
import * as RTConv from '~/features-shared/rtconv/api/rtinterface-senders'

import type { SwymResponse } from '~/interfaces/services-dto'

class RTConvThreads extends Map {
  set(key: string, value: IConversation) {
    const handlerForTopic = {
      /* get(obj: any, prop: string, value: any) {
        if (prop === 'topic') {
          console.log('get prop', prop, typeof value, value)
        }
        return Reflect.get(obj, prop, value)
      }, */
      set(obj: IConversation, prop: string, value: any) {
        if (prop === 'topic' && typeof value === 'string') {
          // console.log('set prop', prop, typeof value, value)
          return Reflect.set(obj, prop, unescapeHTML(value || ''))
        }
        else { return Reflect.set(obj, prop, value) }
      },
    }
    const conversationObject = new Proxy(value, handlerForTopic)
    // do an initial set of the topic to trigger the handler
    conversationObject.topic = value.topic
    return super.set(key, conversationObject)
  }
}

export const useConversationsStoreV2 = defineStore('conversations-v2', () => {
  const storeUsers = useUsersStore()
  const storeMessages = useContentsStoreV2()
  const { context } = useContextManager()
  const { setTempMarks, setFirstMarkSet } = useTemporaryMarks()
  // const REALTIME_NEW_CONTENT_AVAILABLE_TOPIC = 'tenant_{tenant}_new_rtc_content'

  // #region STATES
  const threadsId = reactive<string[]>([])
  const threads = reactive<Map<string, IConversation>>(new RTConvThreads())
  const countMissedCalls = ref(0)
  const maxAllowedUsers = ref(15)
  let threadsFetched = false
  // #endregion

  // #region METHODS
  const getSubjectUriFromId = (id: string) => threadsId.find(el => el.endsWith(`:${id}`)) || id

  const getConversationById = (id: string) => threads.get(getIdFromSubjectUri(id))

  const getMembersFromUsersList = (users: IUser[]): IMember[] => {
    return users.map((user) => {
      return {
        ...user,
        full_name: `${user.first_name} ${user.last_name}`,
        displayName: `${user.first_name} ${user.last_name}`,
        uid: user.login === user.uid ? `iam:${user.uid}` : user.uid,
        format: 'internal',
        external: false,
        contentType: 'person',
        rtcid: user.id,
        state: UserState.Active,
        id: `@${user.first_name} ${user.last_name}`,
      } as IMember
    })
  }

  const setConversation = (conversation: IConversation) => {
    if (!getConversationById(conversation.id))
      threadsId.push(conversation.id)
    threads.set(conversation.id, conversation)

    // Cache members
    const members = getMembersFromUsersList(conversation.users)
    members.forEach(storeUsers.addSearchedUser)
  }

  const getConversationTitle = (id: string, user_list?: boolean) => {
    const dm = getConversationById(id)

    if (dm?.topic && !user_list)
      return dm.topic

    const users = (dm?.users.length === 1
      ? dm?.users
      : dm?.users.filter(el => el.login !== storeUsers.currentUser?.login)) || []
    return users.map(el => `${el.first_name} ${el.last_name}`)
      .join(', ')
  }

  const doFilter = async (filter: string) => {
    if (filter) {
      return [...threads.values()].filter(el => el.topic.toLowerCase()
        .includes(filter.toLowerCase()))
        .map(el => el.id)
    }
    else { return undefined }
  }

  const removeConversation = (conversation: IConversation) => {
    const idx = threadsId.findIndex(id => id === conversation.id)
    if (idx !== -1)
      threadsId.splice(idx, 1)
    threads.delete(conversation.id)
  }

  const getConversationTime = (el: IConversation) => {
    return new Date(el.updated_at || el.created_at || 0)
      .getTime()
  }
  /** Is conversation unread */
  function isConversationUnread(el: IConversation): boolean {
    return el?.last_message ? !(el.mark?.message_id === el.last_message?.message_id) : false
  }
  // #endregion

  // #region GETTERS
  const threadsVisible = computed(() => [...threads.values()].filter(el => !el.is_hidden))
  const countUnread = computed(() => threadsVisible.value.filter(isConversationUnread).length)
  // #endregion

  // #region ACTIONS
  const doCreateConversation = async (users: string[], title?: string) => {
    return RTConv.newConversation({
      users: { logins: users },
      title,
      waitForResponse: true,
    })
  }

  const fetchList = async () => {
    if (threadsFetched)
      return threads.values()

    const result = await RTConv.getConversations({
      waitForResponse: false,
      sections: false,
    })
    threadsFetched = true
    return result
  }

  const fetchConversation = async (id: string) => {
    const conversation_id = getIdFromSubjectUri(id)
    if (threads.has(conversation_id))
      return getConversationById(conversation_id)
    const { conversations } = await RTConv.getConversations({
      convIds: [Number(conversation_id)],
      waitForResponse: true,
    })
    return conversations[0]
  }

  const markAsRead = async (id: string) => {
    const conversation = getConversationById(id)

    // Return early if there's no conversation
    if (!conversation)
      return

    const lastMessageId = conversation.last_message?.message_id

    if (lastMessageId && (lastMessageId !== conversation.mark?.message_id)) {
      setTempMarks(conversation)

      // Set the temporary mark
      const tmp = conversation.mark
      try {
        return RTConv.readConversation({
          convId: Number(conversation.id),
          msgId: Number(lastMessageId),
          waitForResponse: false,
        })
      }
      catch (err) {
        conversation.mark = tmp
        // Throw the error so that it's handled as per need
        throw err
      }
    }
    setFirstMarkSet()
  }

  const updateConversationLastMessage = (
    id: string,
    last_message_id?: string,
    last_message_timestamp?: string,
  ) => {
    const conversation = getConversationById(id)
    if (!conversation)
      return
    conversation.last_message = last_message_id ? { message_id: last_message_id } : undefined
    conversation.updated_at = last_message_timestamp
    if (getIdFromSubjectUri(storeMessages.thread_selected) === id && context.value === EContext.DM)
      conversation.mark = conversation.last_message
  }

  const updateConversationLastMessageRead = (id: string, newMessageId: string) => {
    const conversation = getConversationById(id)
    if (!conversation)
      return
    conversation.mark = { message_id: newMessageId }
  }

  const hideConversation = async (id: string, hide = true) => {
    const conversation = getConversationById(id)
    if (conversation)
      conversation.is_hidden = hide
    return RTConv.setConversationHidden({
      convId: Number(conversation?.id || getIdFromSubjectUri(id)),
      isHidden: hide,
      waitForResponse: false,
    })
  }

  // Toggle RTC mute/unmute
  const toggleMute = (subjectUri: string, state?: boolean) => {
    const conversation = getConversationById(subjectUri)
    if (state === undefined) {
      return RTConv.setConversationMuted({
        convId: Number(subjectUri.split(':')
          .pop()),
        isMuted: !conversation?.is_muted,
        subjectUri,
        waitForResponse: false,
      })
    }
    else {
      return RTConv.setConversationMuted({
        convId: Number(subjectUri.split(':')
          .pop()),
        isMuted: state,
        subjectUri,
        waitForResponse: false,
      })
    }
  }

  const setTopic = async (id: string, topic?: string) => {
    const title = !topic ? null : topic
    const conversation = getConversationById(id)
    if (conversation)
      return RTConv.updateConvTitle({ convId: Number(conversation.id), title })
  }

  const getMembers = async (
    id: string,
    page?: number,
    search?: string,
  ) => {
    const conversation = getConversationById(id)
    if (!conversation) {
      const users = await RTConv.getConvMembers({
        convId: Number(getIdFromSubjectUri(id)),
        waitForResponse: true,
      })
      const members = getMembersFromUsersList(users)
      members.forEach(storeUsers.addSearchedUser)
      return { users: members, total: members.length }
    }

    const members = getMembersFromUsersList(conversation.users)
    members.forEach(storeUsers.addSearchedUser)
    if (search) {
      const { default: Fuse } = await import('fuse.js')
      const membersFiltered = new Fuse(members, {
        threshold: 0.2,
        keys: ['login', 'first_name', 'last_name', 'full_name'] as (keyof IMember)[],
        isCaseSensitive: false,
        ignoreLocation: true,
        ignoreFieldNorm: true,
      })
        .search(search)
        .map(el => el.item)
      return { users: membersFiltered, total: membersFiltered.length }
    }
    else { return { users: members, total: members.length } }
  }

  const doAddMembersToConversation = async (id: string, users: string[]) => {
    const conversation = getConversationById(id)
    if (conversation) {
      return RTConv.newConversationUsers({
        convId: Number(conversation.id),
        users: { logins: users },
        waitForResponse: true,
      })
    }
  }

  const doRemoveMemberFromConversation = async (id: string, user: IUser) => {
    const conversation = getConversationById(id)
    if (conversation && user.rtcid) {
      return RTConv.removeConversationUser({
        convId: Number(conversation.id),
        // eslint-disable-next-line no-warning-comments
        userId: Number(user.rtcid), // todo use id instead of rtcid, which means changing getMembersFromUsersList
        waitForResponse: false,
      })
    }
  }

  const transformConvToCommunity = async (
    data: CreateCommunityData,
    conversation: IConversation,
  ): Promise<SwymResponse<ICommunity>> => {
    const community = await transformRTConvToCommunity(data, conversation.subject_uri)
    community.result.is_hidden = false
    const index = threadsId.indexOf(conversation.subject_uri)
    if (index > -1)
      threadsId.splice(index, 1)
    threads.delete(conversation.subject_uri)
    return community
  }

  const setPersistency = async (id: string, autoRemovalEnabled: boolean) => {
    const conversation = getConversationById(id)
    if (conversation) {
      return RTConv.autoRemoval({
        convId: Number(conversation.id),
        autoRemovalEnabled,
        waitForResponse: false,
      })
    }
  }

  const resetMissedBadge = async () => {
    if (countMissedCalls.value !== 0)
      RTConv.resetMissedBadge({ waitForResponse: false })
  }

  /**
   * Retrieves the maximum allowed users set in RTC
   *
   * @returns The value fetched from the service
   */
  async function fetchMaxAllowedUsers() {
    maxAllowedUsers.value = await getMaxUsersAllowed()
    return maxAllowedUsers.value
  }

  /**
   * Retrieve the RTConversation id from the legacy URI of the conversation after the migration
   * @param legacyId Legacy content URI of the conversation before migration
   * @returns Id of the RTConversation
   */
  async function fetchRTCIdFromLegacyConversation(legacyId: string) {
    return getRTConversationId(legacyId)
  }
  // #endregion

  return {
    // #region STATES
    threads,
    threadsId,
    countMissedCalls,
    maxAllowedUsers,
    // #endregion

    // #region COMPUTED
    countUnread,
    threadsVisible,
    // #endregion

    // #region METHODS
    getSubjectUriFromId,
    getConversationById,
    getConversationBySubjectUri: getConversationById,
    setConversation,
    getConversationTitle,
    removeConversation,
    getConversationTime,
    isConversationUnread,
    // #endregion

    // #region ACTIONS
    doFilter,
    doCreateConversation,
    fetchList,
    fetchConversation,
    markAsRead,
    updateConversationLastMessage,
    updateConversationLastMessageRead,
    hideConversation,
    toggleMute,
    setTopic,
    getMembers,
    doAddMembersToConversation,
    doRemoveMemberFromConversation,
    transformConvToCommunity,
    setPersistency,
    resetMissedBadge,
    fetchMaxAllowedUsers,
    fetchRTCIdFromLegacyConversation,
    // #endregion
  }
})
